/**
 * Phone Log Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Phone, Save } from "lucide-react";
import { toast } from "sonner";
import SpeechDictation from "../SpeechDictation";

export function PhoneLogOverlay({ patientId, patientName, onSave, onClose }) {
  const [formData, setFormData] = useState({
    call_type: "outgoing",
    contact_name: "",
    contact_relationship: "",
    phone_number: "",
    call_purpose: "",
    call_summary: "",
    follow_up_required: false,
    follow_up_date: "",
  });

  const handleSave = () => {
    if (!formData.call_purpose || !formData.call_summary) {
      toast.error("Please fill in call purpose and summary");
      return;
    }
    toast.success("Phone log saved successfully");
    if (onSave) onSave(formData);
    if (onClose) onClose();
  };

  return (
    <div className="space-y-4 p-4" data-testid="phone-log-overlay">
      <div className="flex items-center gap-2 mb-4">
        <Phone className="w-5 h-5 text-teal-400" />
        <h3 className="font-semibold text-white">Phone Log</h3>
        <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Call Type</Label>
          <Select value={formData.call_type} onValueChange={(v) => setFormData({...formData, call_type: v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="outgoing">Outgoing</SelectItem>
              <SelectItem value="incoming">Incoming</SelectItem>
              <SelectItem value="voicemail">Voicemail Left</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Contact Name</Label>
          <Input 
            value={formData.contact_name} 
            onChange={(e) => setFormData({...formData, contact_name: e.target.value})} 
            placeholder="Who did you speak with?" 
            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Relationship</Label>
          <Select value={formData.contact_relationship} onValueChange={(v) => setFormData({...formData, contact_relationship: v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="patient">Patient</SelectItem>
              <SelectItem value="spouse">Spouse</SelectItem>
              <SelectItem value="child">Child</SelectItem>
              <SelectItem value="caregiver">Caregiver</SelectItem>
              <SelectItem value="physician">Physician</SelectItem>
              <SelectItem value="pharmacy">Pharmacy</SelectItem>
              <SelectItem value="facility">Facility</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Phone Number</Label>
          <Input 
            value={formData.phone_number} 
            onChange={(e) => setFormData({...formData, phone_number: e.target.value})} 
            placeholder="(555) 123-4567" 
            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Call Purpose</Label>
        <Select value={formData.call_purpose} onValueChange={(v) => setFormData({...formData, call_purpose: v})}>
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Select purpose..." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="symptom_report">Symptom Report</SelectItem>
            <SelectItem value="medication_question">Medication Question</SelectItem>
            <SelectItem value="scheduling">Scheduling</SelectItem>
            <SelectItem value="care_coordination">Care Coordination</SelectItem>
            <SelectItem value="family_update">Family Update</SelectItem>
            <SelectItem value="equipment_issue">Equipment Issue</SelectItem>
            <SelectItem value="emergency">Emergency/Urgent</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-slate-200">Call Summary</Label>
          <SpeechDictation onTranscript={(t) => setFormData({...formData, call_summary: formData.call_summary + " " + t})} buttonSize="sm" />
        </div>
        <Textarea 
          value={formData.call_summary} 
          onChange={(e) => setFormData({...formData, call_summary: e.target.value})} 
          placeholder="Document the conversation..." 
          rows={4} 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
        <div>
          <Label className="text-slate-200">Follow-up Required?</Label>
          <p className="text-xs text-slate-400">Schedule a callback or task</p>
        </div>
        <Switch checked={formData.follow_up_required} onCheckedChange={(c) => setFormData({...formData, follow_up_required: c})} />
      </div>

      {formData.follow_up_required && (
        <div className="space-y-2">
          <Label className="text-slate-200">Follow-up Date</Label>
          <Input 
            type="datetime-local" 
            value={formData.follow_up_date} 
            onChange={(e) => setFormData({...formData, follow_up_date: e.target.value})} 
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
        <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700"><Save className="w-4 h-4 mr-1" />Save Log</Button>
      </div>
    </div>
  );
}

export default PhoneLogOverlay;
